import { Link, useMatches, useNavigate } from "@tanstack/react-router";
import { Bell, BookOpen, Check, ChevronDown, ChevronRight, Contrast, FolderKanban, GitPullRequest, Keyboard, LogOut, Menu, Monitor, Moon, Search, ShieldHalf, Sun, UserRound } from "lucide-react";
import {
  Avatar,
  Badge,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  Kbd,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RelativeTime,
  ActorChip,
  EmptyState,
} from "@/design";
import { api, useApprovals, useEnvironments, useMe, useProjectStatus, type Environment } from "@/lib/api";
import { cn } from "@/lib/cn";
import { adminElsewhere, adminUrl, appUrl, onAdminHost } from "@/lib/adminHost";
import { docsUrl } from "@/lib/config";
import { openCommandPalette } from "./commands";
import { useEnvName, useProjectSlug, useWorkspaceSlug } from "./context";
import { openShortcuts } from "./Shortcuts";
import { useTheme, type ThemePref } from "./theme";

export function TopBar({ onMenu }: { onMenu: () => void }) {
  const project = useProjectSlug();
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-canvas/85 px-3 backdrop-blur-md supports-[backdrop-filter]:bg-canvas/70 sm:px-4 lg:px-6">
      <IconButton label="Open menu" className="lg:hidden" onClick={onMenu}>
        <Menu />
      </IconButton>
      <Breadcrumbs />
      {project && <EnvironmentSwitcher />}
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          onClick={openCommandPalette}
          className="mr-1 hidden h-8 w-64 items-center gap-2 rounded-md border border-border bg-surface px-2.5 text-sm text-subtle shadow-xs transition-colors hover:border-border-strong hover:text-muted md:flex"
        >
          <Search className="size-4" />
          <span className="flex-1 text-left">Search or jump to…</span>
          <Kbd mod>K</Kbd>
        </button>
        <IconButton label="Search" className="md:hidden" onClick={openCommandPalette}>
          <Search />
        </IconButton>
        <ApprovalsBell />
        <ThemeMenu />
        <UserMenu />
      </div>
    </header>
  );
}

function Breadcrumbs() {
  const workspace = useWorkspaceSlug();
  const project = useProjectSlug();
  const status = useProjectStatus(project);
  const matches = useMatches();
  const leaf = [...matches].reverse().find((m) => m.staticData?.title)?.staticData.title;
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-base">
      {project ? (
        <>
          <Link to="/$workspace" params={{ workspace }} className="hidden truncate text-muted hover:text-text sm:block">
            Projects
          </Link>
          <ChevronRight className="hidden size-3.5 shrink-0 text-subtle sm:block" />
          <Link to="/$workspace/$project" params={{ workspace, project }} search={((s: Record<string, unknown>) => s) as never} className="truncate font-semibold text-text hover:text-accent-text">
            {status.data?.project.name ?? project}
          </Link>
          {leaf && leaf !== "Canvas" && (
            <>
              <ChevronRight className="size-3.5 shrink-0 text-subtle" />
              <span className="truncate text-muted" aria-current="page">
                {leaf}
              </span>
            </>
          )}
        </>
      ) : (
        <span className="truncate font-semibold text-text" aria-current="page">
          {leaf ?? "Projects"}
        </span>
      )}
    </nav>
  );
}

const envDot: Record<Environment["kind"], string> = {
  production: "bg-accent",
  staging: "bg-warn",
  preview: "bg-info",
  development: "bg-idle",
};

function EnvironmentSwitcher() {
  const project = useProjectSlug();
  const envName = useEnvName();
  const navigate = useNavigate();
  const envs = useEnvironments(project);
  const list = envs.data?.data ?? [];
  const current = list.find((e) => e.name === envName);
  const choose = (name: string) => navigate({ to: ".", search: ((s: Record<string, unknown>) => ({ ...s, env: name === "production" ? undefined : name })) as never });
  const groups: [string, Environment[]][] = [
    ["Environments", list.filter((e) => e.kind !== "preview")],
    ["Previews", list.filter((e) => e.kind === "preview")],
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "ml-2 inline-flex h-8 items-center gap-2 rounded-full border border-border bg-surface pl-2.5 pr-2 text-sm font-medium text-text shadow-xs transition-colors hover:border-border-strong",
          current?.kind === "production" && "border-accent/30",
        )}
        aria-label={`Environment: ${envName}`}
      >
        <span className={cn("size-2 rounded-full", envDot[current?.kind ?? "production"])} aria-hidden="true" />
        <span className="max-w-32 truncate">{envName}</span>
        {current?.protection?.require_approval && (
          <Badge size="sm" tone="accent" className="hidden sm:inline-flex">
            Protected
          </Badge>
        )}
        <ChevronDown className="size-3.5 text-subtle" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        {groups.map(([heading, items]) =>
          items.length ? (
            <div key={heading}>
              <DropdownMenuLabel>{heading}</DropdownMenuLabel>
              {items.map((e) => (
                <DropdownMenuItem key={e.id} onSelect={() => choose(e.name)}>
                  <span className={cn("size-2 rounded-full", envDot[e.kind])} aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{e.name}</span>
                    {e.pull_request ? (
                      <span className="flex items-center gap-1 truncate text-xs text-muted">
                        <GitPullRequest className="size-3! text-muted!" /> #{e.pull_request.number} {e.pull_request.title}
                      </span>
                    ) : e.branch ? (
                      <span className="block truncate text-xs text-muted">from {e.branch}</span>
                    ) : null}
                  </span>
                  {e.name === envName && <Check className="text-accent-text!" />}
                </DropdownMenuItem>
              ))}
            </div>
          ) : null,
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ApprovalsBell() {
  const workspace = useWorkspaceSlug();
  const approvals = useApprovals(workspace, { status: "pending" });
  const items = approvals.data?.data ?? [];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex size-9 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-text"
          aria-label={items.length ? `${items.length} approvals waiting for you` : "Approvals"}
        >
          <Bell className="size-[18px]" />
          {items.length > 0 && (
            <span className="absolute right-1.5 top-1.5 flex size-4 items-center justify-center rounded-full bg-warn text-[10px] font-bold text-text ring-2 ring-canvas">{items.length}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="border-b border-border px-4 py-3">
          <div className="text-md font-semibold">Waiting for you</div>
          <div className="text-sm text-muted">Agents ask before doing anything risky.</div>
        </div>
        {items.length === 0 ? (
          <EmptyState size="sm" title="Nothing to approve" description="When an agent needs your go-ahead, it appears here and on your phone." />
        ) : (
          <ul className="max-h-96 divide-y divide-border overflow-y-auto">
            {items.map((a) => (
              <li key={a.id}>
                <Link to="/approve/$approvalId" params={{ approvalId: a.id }} className="block px-4 py-3 hover:bg-surface-2">
                  <div className="text-base font-medium text-text">{a.action.summary}</div>
                  <div className="mt-1.5 flex items-center justify-between gap-2 text-sm text-muted">
                    <ActorChip actor={a.requested_by} size="sm" />
                    <RelativeTime value={a.created_at} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function ThemeMenu() {
  const { theme, resolved, contrast, setTheme, setContrast } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton label="Theme" noTooltip>
          {resolved === "dark" ? <Moon /> : <Sun />}
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as ThemePref)}>
          <DropdownMenuRadioItem value="light">
            <Sun className="text-muted" /> Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="text-muted" /> Dark
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor className="text-muted" /> Match my device
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem checked={contrast === "high"} onCheckedChange={(c) => setContrast(c ? "high" : "normal")}>
          <Contrast className="text-muted" /> High contrast
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function UserMenu() {
  const me = useMe();
  const navigate = useNavigate();
  const user = me.data?.user;
  // Outside a workspace (the admin console) account settings live under the first workspace.
  const workspace = useWorkspaceSlug() || me.data?.workspaces[0]?.slug || "";
  const inAdmin = useMatches().some((m) => m.routeId.startsWith("/admin"));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="ml-1 rounded-full" aria-label="Your account">
        <Avatar name={user?.name ?? "?"} size="md" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="px-2 py-2">
          <div className="truncate text-base font-semibold">{user?.name}</div>
          <div className="truncate text-sm text-muted">{user?.email}</div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<UserRound />}
          onSelect={() => (onAdminHost ? window.location.assign(appUrl(`/${workspace}/account`)) : navigate({ to: "/$workspace/account", params: { workspace } }))}
        >
          Your account
        </DropdownMenuItem>
        {user?.is_platform_admin &&
          (inAdmin ? (
            workspace && (
              <DropdownMenuItem icon={<FolderKanban />} onSelect={() => (onAdminHost ? window.location.assign(appUrl(`/${workspace}`)) : navigate({ to: "/$workspace", params: { workspace } }))}>
                Back to your projects
              </DropdownMenuItem>
            )
          ) : (
            <DropdownMenuItem icon={<ShieldHalf />} onSelect={() => (adminElsewhere ? window.location.assign(adminUrl("/admin")) : navigate({ to: "/admin" }))}>
              Platform admin
            </DropdownMenuItem>
          ))}
        <DropdownMenuItem icon={<Keyboard />} shortcut="?" onSelect={openShortcuts}>
          Keyboard shortcuts
        </DropdownMenuItem>
        <DropdownMenuItem icon={<BookOpen />} onSelect={() => window.open(docsUrl("/docs"), "_blank")}>
          Documentation
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<LogOut />}
          onSelect={async () => {
            await api.POST("/v1/auth/logout", { params: { header: { "Idempotency-Key": crypto.randomUUID() } } }).catch(() => undefined);
            void navigate({ to: "/login" });
          }}
        >
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

